import React, { useState } from "react";
import useApi from "@/hooks/useApi";
import useSharedSchedules from "@/hooks/useSharedSchedules";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import { SharedSchedule, ScheduleWindow } from "@/context/SharedSchedulesContext";
import ScheduleEditor from "./ScheduleEditor";

type Mode = "list" | "create" | "edit";

export default function Schedules() {
  const api = useApi();
  const { schedules, loading, reload } = useSharedSchedules();
  const { displayToast } = useToastAlert();

  const [mode, setMode] = useState<Mode>("list");
  const [editTarget, setEditTarget] = useState<SharedSchedule | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [windows, setWindows] = useState<ScheduleWindow[]>([]);

  function startCreate() {
    setName("");
    setTimezone("");
    setWindows([
      { daysOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], fromTime: "09:00", toTime: "17:00" }
    ]);
    setEditTarget(null);
    setMode("create");
  }

  function startEdit(schedule: SharedSchedule) {
    setName(schedule.name);
    setTimezone(schedule.timezone ?? "");
    setWindows(schedule.windows ?? []);
    setEditTarget(schedule);
    setMode("edit");
  }

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const body = { name: name.trim(), timezone: timezone || null, windows };
      if (mode === "create") {
        await api.messaging.post("SharedSchedule", body);
        displayToast(Severity.Success, "Schedule created.");
      } else if (mode === "edit" && editTarget) {
        await api.messaging.put(`SharedSchedule/${editTarget.id}`, body);
        displayToast(Severity.Success, "Schedule updated.");
      }
      await reload();
      setMode("list");
    } catch {
      displayToast(Severity.Error, "Failed to save schedule.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(schedule: SharedSchedule) {
    if (!confirm(`Delete "${schedule.name}"? Any teams or rules using this schedule will lose the link.`)) return;
    try {
      await api.messaging.delete(`SharedSchedule/${schedule.id}`);
      displayToast(Severity.Success, "Schedule deleted.");
      await reload();
    } catch {
      displayToast(Severity.Error, "Failed to delete schedule.");
    }
  }

  if (mode === "create" || mode === "edit") {
    return (
      <div className="p-6 max-w-2xl">
        <h2 className="text-lg font-semibold mb-4">
          {mode === "create" ? "New Schedule" : `Edit: ${editTarget?.name}`}
        </h2>
        <ScheduleEditor
          name={name}
          timezone={timezone}
          windows={windows}
          onNameChange={setName}
          onTimezoneChange={setTimezone}
          onWindowsChange={setWindows}
          onSave={handleSave}
          onCancel={() => setMode("list")}
          saving={saving}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-slate-200 bg-white">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Schedules</h1>
          <p className="text-xs text-slate-400 mt-0.5">Define business hours and availability windows</p>
        </div>
        <button
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
          onClick={startCreate}
        >
          + New Schedule
        </button>
      </div>
      <div className="p-6">

      {loading ? (
        <p className="text-slate-400">Loading…</p>
      ) : schedules.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-400">
          <p className="text-sm">No schedules yet.</p>
          <p className="text-xs mt-1">
            Create a named schedule like "Business Hours" and link it to teams and routing rules.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3 max-h-[600px] overflow-y-auto">
          {schedules.map((s) => (
            <div
              key={s.id}
              className="border border-slate-200 rounded-lg p-4 flex items-start justify-between hover:shadow-md hover:border-[var(--color-primary)]/50 transition-all bg-white"
            >
              <div>
                <div className="font-medium text-slate-800">{s.name}</div>
                {s.timezone && (
                  <div className="text-xs text-slate-400 mt-0.5">{s.timezone}</div>
                )}
                <div className="text-sm text-slate-500 mt-1">
                  {(s.windows ?? []).length === 0
                    ? "No time windows defined"
                    : (s.windows ?? []).map((w, i) => (
                        <span key={i} className="mr-2">
                          {w.daysOfWeek.map((d) => d.slice(0, 3)).join(", ")}{" "}
                          {w.fromTime}–{w.toTime}
                        </span>
                      ))}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 text-sm hover:bg-slate-50"
                  onClick={() => startEdit(s)}
                >
                  Edit
                </button>
                <button
                  className="text-red-500 hover:text-red-700 text-sm"
                  onClick={() => handleDelete(s)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
